import { createApp } from "vue/dist/vue.esm-bundler";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import MasterLayout from "./components/MasterLayout.vue";
import { createLogoutUri, redirectDeepLinkPath } from "./utils/redirect-uri";
import { Browser } from "@capacitor/browser";

import { IonicVue } from "@ionic/vue";

/* Core CSS required for Ionic components to work properly */
import "@ionic/vue/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/vue/css/normalize.css";
import "@ionic/vue/css/structure.css";
import "@ionic/vue/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/vue/css/padding.css";
import "@ionic/vue/css/float-elements.css";
import "@ionic/vue/css/text-alignment.css";
import "@ionic/vue/css/text-transformation.css";
import "@ionic/vue/css/flex-utils.css";
import "@ionic/vue/css/display.css";
import "tailwindcss/base.css";
import "tailwindcss/components.css";
import "tailwindcss/utilities.css";

/* Theme variables */
import "./theme/variables.css";

/* TailwindCSS */
import "./styles/main.scss";

import { auth0 } from "./utils/auth0";
import HtmlToVue from "./components/common/HtmlToVue.vue";
import { datadogLogs } from "@datadog/browser-logs";
import InAppBrowser from "./components/InAppBrowser.vue";
import youtubePlayer from "./utils/youtube-player";
import Vueform from "@vueform/vueform";
import vueformConfig from "./../vueform.config";
import i18n from "./utils/i18n";
import { globalCookiesConfig } from "vue3-cookies";

globalCookiesConfig({
  expireTimes: "1d",
  path: "/",
  domain: "",
  secure: true,
  sameSite: "None",
});

(async () => {
  const app = createApp(App)
    .use(IonicVue)
    .use(auth0)
    .use(router)
    .use(store)
    .use(youtubePlayer)
    .use(i18n)
    .use(Vueform, vueformConfig)
    .component("master-layout", MasterLayout)
    .component("html-to-vue", HtmlToVue)
    .component("in-app-browser", InAppBrowser);

  if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
    datadogLogs.init({
      clientToken: process.env.VUE_APP_DATADOG_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: process.env.VUE_APP_DATADOG_SERVICE,
      env: process.env.VUE_APP_NODE_ENV,
      forwardErrorsToLogs: true,
      sampleRate: 100,
    });
  }

  router.isReady().then(() => {
    if (process.env.VUE_APP_NODE_ENV === "local") {
      const token = localStorage.getItem("token");
      if (token) store.commit("user/setToken", token);
      app.mount("#app");
      return;
    }

    app.mount("#app");
  });

  app.config.errorHandler = async function (err, vm, info) {
    console.log("in error handler");
    console.error(err);

    // TODO datadog error monitoring
    if (err.message === "Request failed with status code 401") {
      await auth0.logout({
        logoutParams: {
          returnTo: createLogoutUri(
            process.env.VUE_APP_PACKAGE_ID,
            process.env.VUE_APP_AUTH0_DOMAIN
          ),
        },
        openUrl: async (url) =>
          await Browser.open({
            url,
            windowName: "_self",
          }),
      });
    }
  };

  window.onerror = function (message, source, lineno, colno, error) {
    console.error("in window error handler");
    console.error(message);
    console.error(error);
    // TODO datadog error monitoring
  };

  window.addEventListener("unhandledrejection", async (event) => {
    console.error("in unhandled rejecction");
    console.error(event);
    const errorMessage = event.reason?.message || event.reason?.toString() || JSON.stringify(event.reason);
    console.error(errorMessage);
    
    // Ignore "Load failed" errors from Auth0 callback handling with custom URL schemes
    if (errorMessage === "Load failed" || errorMessage?.includes("Load failed") || 
        (typeof event.reason === "object" && event.reason?.isTrusted === false && !event.reason?.message)) {
      console.log("Ignoring Load failed error from Auth0 callback (expected for custom URL schemes)");
      event.preventDefault(); // Prevent the error from being logged as unhandled
      return;
    }
    if (
      event.reason &&
      event.reason.message &&
      (event.reason.message === "Request failed with status code 401" ||
        event.reason.message === "Login required" ||
        event.reason.message.startsWith("Missing Refresh Token") ||
        event.reason.message.startsWith("Unknown or invalid refresh token"))
    ) {
      await auth0.logout({
        logoutParams: {
          returnTo: createLogoutUri(
            process.env.VUE_APP_PACKAGE_ID,
            process.env.VUE_APP_AUTH0_DOMAIN
          ),
        },
        openUrl: async (url) =>
          await Browser.open({
            url,
            windowName: "_self",
          }),
      });
    }

    if (
      event.reason &&
      event.reason.message &&
      event.reason.message.includes("nvalid authorization code")
    ) {
      console.error(event.reason.message)
      console.warn("Detected invalid authorization code. Refreshing page...");
      window.location.reload();
    }
    // TODO datadog error monitoring
  });
})();
